import React, { useState } from 'react';
import styles from './SendForm.module.css';
import { useAppDispatch, useAppSelector } from 'src/hooks/hooks';
import { selectSession } from 'src/store/session/sessionSelects';
import { DesignStatus } from 'src/enums/design-status';
import { sendDesignStatusAsync } from 'src/store/status/actionStatus';
import { RequestStatus } from 'src/enums/request-status';
import { useTranslation } from 'react-i18next';
import { selectRequestStatus } from 'src/store/status/statusSelects';
import { useResize } from 'src/hooks/useResize/useResize';
import { shallowEqual } from 'react-redux';
import { Loader, LoaderSize } from 'src/components/loader/Loader';
import { Textbox } from 'src/components/textbox/Textbox';
import { setDesignStatus } from 'src/store/design/designSlice';
import biLoggingService, { ActionType, ObjectType, UserActionData } from 'src/services/bi-logging/bi-logging.service';
import { Button } from 'src/components/button/Button';

export interface TextBoxState {
    title: string;
    message?: string;
}

export interface SendProps {
    onBack: () => void;
    status: DesignStatus;
}

export function SendForm(props: SendProps) {
    const {status, onBack} = props;
    const dispatch = useAppDispatch();
    const session = useAppSelector(selectSession);
    const requestStatus = useAppSelector(selectRequestStatus, shallowEqual);
    const size = useResize();
    const {t} = useTranslation();
    const [messageState, setMessageState] = useState<TextBoxState>({
        title:
            status === DesignStatus.Approved
                ? t('SharedDesignAction.Title.ApproveDesign')
                : t('SharedDesignAction.Title.ReturnDesign'),
        message: '',
    });

    const locationName = 'exocad-3d-viewer page';
    const objectNameBack = 'back_button';
    const objectNameInput = 'message_input';
    const objectNameSend = 'send_button';

    if (requestStatus === RequestStatus.Completed) {
        dispatch(setDesignStatus(status));
    }

    const isLoading = () => requestStatus === RequestStatus.Loading;

    const sendStatusEvent = () => {
        if (!isLoading()) {
            dispatch(
                sendDesignStatusAsync({
                    ...session,
                    status: status,
                    message: messageState.message
                })
            );
        }
    };

    const messageFormTitle = () => {
        return size.isScreenSm ? <span className={styles.title}>{messageState.title}</span> : null;
    };

    const sendButtonContent = () => {
        return !isLoading() ? (
            t('SharedDesignAction.Button.Send')
        ) : (
            <Loader id="e3dv_send_loader" show={true} size={LoaderSize.small}/>
        );
    };

    const sendEvent = (objectName: string,
                       action: ActionType,
                       objectType: ObjectType,
                       selectedValue: string = '',
                       locationParentObject: string = locationName,
                       automationId: string = '') => {
        const userActionData: UserActionData = {
            objectName,
            action,
            objectType,
            locationParentObject,
            selectedValue,
            automationId
        };
        biLoggingService.sendUserAction(userActionData);
    }

    const sendForm = () => {
        return (
            <div className={`${styles.form}`}>
                <div
                    auto-id="e3dv_status_back"
                    data-testid="back_button"
                    className={`${isLoading() ? styles.disabled : ''} ${styles.arrow} `}
                    onClick={() => {
                        !isLoading() && onBack();
                        sendEvent(objectNameBack, ActionType.Click, ObjectType.Button);
                    }}></div>
                {messageFormTitle()}

                <div className={styles.body}>
                    <Textbox
                        id="e3dv_status_notes"
                        className={styles.textbox}
                        value={messageState.message}
                        maxLength={1000}
                        disabled={isLoading()}
                        onChangeValue={message => {
                            setMessageState({...messageState, message});
                        }}
                    />
                    <Button
                        id="e3dv_status_send"
                        className={`${
                            status === DesignStatus.Approved ? styles.blue : styles.red
                        } ${styles.send}`}
                        name={sendButtonContent()}
                        disabled={isLoading()}
                        onClick={() => {
                            sendStatusEvent()
                            sendEvent(objectNameInput, ActionType.Input, ObjectType.Input, messageState.message);
                            sendEvent(objectNameSend, ActionType.Click, ObjectType.Button);
                        }}
                    />
                </div>
            </div>
        );
    };

    return sendForm();
}
